/* rv-jar-panel: the "What's in the jar" panel (Cris, 2026-10-02; homepage buy box 2026-10-04).
   Server-rendered as accordions (<details data-rv-jar-tab="KEY">), so everything works without JS. This upgrades them:
   each row becomes a button that opens one <dialog>, with a tab per pane. A row holds one or more panes
   ([data-rv-jar-pane="k"]); each moves into the dialog under the tab [role="tab"][data-rv-jar-tab="k"], and the row's
   button opens the row's own KEY.
   - Product pages (sections/rv-main-product.liquid): two rows (Ingredients, Nutrition Facts), one pane each. A sheet
     from the bottom on phones, a centered pop-up from 990px.
   - Homepage buy box (snippets/rv-buy-box.liquid): one row ("Nutrition Facts & ingredients") carrying both panes, in a
     <dialog class="rv-jar rv-jar--side">: a drawer from the right from 990px, the same sheet on phones. Its Ingredients
     pane holds one [data-rv-show] wrapper per option, which assets/rv-buy-box.js shows and hides. So this script only
     ever sets `hidden` on the panes themselves (and, once at start, on a tab with no pane and on the tab list when a
     single tab is left).
   A row is upgraded only when every pane in it has its own tab; otherwise it stays an accordion, content and all.
   Esc, the close button and a tap on the dimmed page close it; the native dialog keeps focus inside and returns it to
   the button that opened it. Tabs follow the WAI-ARIA pattern (arrows, Home, End). Motion: transform/opacity only; the
   close waits for the panel's own CSS transition (read from the computed style), none for prefers-reduced-motion. */
(() => {
  if (customElements.get('rv-jar-panel')) return;
  const D = document;
  const OPEN_CLASS = 'rv-jar-open';
  const sel = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : v);
  /* the page's scroll lock. Where scrollbars take up width (Windows, Linux), hiding the scrollbar would shift the page
     sideways each time the panel opens: its width is given back as padding (html.rv-jar-open, rv-jar-panel.css), so the
     page stays put and the panel and its dimmed backdrop still reach the screen's right edge. 0 with overlay scrollbars. */
  const lock = (on) => {
    const root = D.documentElement;
    if (!on) {
      root.classList.remove(OPEN_CLASS);
      root.style.removeProperty('--rv-jar-sb');
      return;
    }
    if (root.classList.contains(OPEN_CLASS)) return; /* measured once, while the scrollbar is still there */
    const sb = Math.max(0, window.innerWidth - root.clientWidth);
    if (sb) root.style.setProperty('--rv-jar-sb', `${sb}px`);
    root.classList.add(OPEN_CLASS);
  };

  customElements.define(
    'rv-jar-panel',
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        const dlg = this.querySelector('dialog.rv-jar');
        const rows = [...this.querySelectorAll('details[data-rv-jar-tab]')];
        const body = dlg && dlg.querySelector('.rv-jar__body');
        if (!dlg || !body || !rows.length || typeof dlg.showModal !== 'function') return;
        this.ready = true;
        this.dlg = dlg;
        this.rm = matchMedia('(prefers-reduced-motion: reduce)');
        const tablist = dlg.querySelector('[role="tablist"]');
        this.tabs = [];

        rows.forEach((row) => {
          const key = row.dataset.rvJarTab;
          const summary = row.querySelector('summary');
          if (!summary) return;
          /* the row's panes (outermost only): one on the product pages, several on the homepage */
          const panes = [...row.querySelectorAll('[data-rv-jar-pane]')].filter(
            (p) => !p.parentElement.closest('[data-rv-jar-pane]')
          );
          /* pair every pane with its tab BEFORE moving anything: if one has no tab, or a key repeats, the whole row
             stays a plain accordion (the row is replaced below, so a pane left behind would vanish with it) */
          const pairs = panes.map((pane) => {
            const k = pane.dataset.rvJarPane || key;
            return { k, pane, tab: dlg.querySelector(`[role="tab"][data-rv-jar-tab="${sel(k)}"]`) };
          });
          const ok =
            pairs.length > 0 &&
            pairs.every(
              (p, i) => p.tab && pairs.findIndex((q) => q.k === p.k) === i && !this.tabs.some((t) => t.key === p.k)
            );
          if (!ok) return;
          const first = pairs[0].k;
          pairs.forEach(({ k, pane, tab }) => {
            pane.classList.remove('rv-acc__body');
            pane.classList.add('rv-jar__pane');
            pane.id = tab.getAttribute('aria-controls');
            pane.setAttribute('role', 'tabpanel');
            pane.setAttribute('aria-labelledby', tab.id);
            pane.tabIndex = 0;
            pane.hidden = true;
            body.append(pane);
            tab.addEventListener('click', () => this.select(k));
            this.tabs.push({ key: k, tab, pane });
          });
          /* the row becomes a real button with the same look (and keeps the theme editor's block hooks) */
          const btn = D.createElement('button');
          btn.type = 'button';
          btn.className = summary.className;
          btn.setAttribute('aria-haspopup', 'dialog');
          btn.innerHTML = summary.innerHTML;
          const wrap = D.createElement('div');
          wrap.className = row.className;
          [...row.attributes].forEach((a) => {
            if (a.name.startsWith('data-shopify') || a.name === 'id') wrap.setAttribute(a.name, a.value);
          });
          wrap.append(btn);
          row.replaceWith(wrap);
          btn.addEventListener('click', () => this.open(this.tabs.some((t) => t.key === key) ? key : first));
        });
        if (!this.tabs.length) return;
        /* arrow keys follow the tabs as they appear on screen */
        this.tabs.sort((a, b) => (a.tab.compareDocumentPosition(b.tab) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
        /* a tab whose row stayed an accordion has nothing to show: hide it (set once, never toggled) */
        dlg.querySelectorAll('[role="tab"]').forEach((t) => {
          if (!this.tabs.some((x) => x.tab === t)) t.hidden = true;
        });
        if (tablist && this.tabs.length < 2) tablist.hidden = true;

        if (tablist) {
          tablist.addEventListener('keydown', (e) => {
            const i = this.tabs.findIndex((t) => t.tab === D.activeElement);
            if (i < 0) return;
            const n = this.tabs.length;
            const to = { ArrowRight: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, Home: 0, End: n - 1 }[e.key];
            if (to === undefined) return;
            e.preventDefault();
            this.select(this.tabs[to].key, true);
          });
        }
        const x = dlg.querySelector('.rv-jar__x');
        if (x) x.addEventListener('click', () => this.close());
        dlg.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.close();
        });
        /* a click on the dimmed page lands on the <dialog> itself (its box has no padding) */
        dlg.addEventListener('click', (e) => {
          if (e.target === dlg) this.close();
        });
        /* however it closed (also a browser's forced close on a repeated Esc), leave the page unlocked */
        dlg.addEventListener('close', () => {
          if (dlg.open) return;
          clearTimeout(this.timer);
          this.closing = false;
          dlg.classList.remove('is-in');
          lock(false);
        });
        /* a link to this panel's id (#label: the Science page's sodium note and the shop's sources line) opens the
           Nutrition Facts (links audit 2026-10-11; before, the anchor had no target) */
        if (this.id) {
          const go = () => {
            if (location.hash !== `#${this.id}` || this.dlg.open) return;
            this.open(this.tabs.some((t) => t.key === 'nutrition') ? 'nutrition' : this.tabs[0].key);
          };
          window.addEventListener('hashchange', go);
          go();
        }
      }

      disconnectedCallback() {
        if (this.dlg && this.dlg.open) lock(false);
      }

      select(key, focus) {
        this.tabs.forEach((t) => {
          const on = t.key === key;
          t.tab.setAttribute('aria-selected', String(on));
          t.tab.tabIndex = on ? 0 : -1;
          t.pane.hidden = !on;
          if (on && focus) t.tab.focus();
        });
        const body = this.dlg.querySelector('.rv-jar__body');
        if (body) body.scrollTop = 0;
      }

      open(key) {
        clearTimeout(this.timer);
        this.closing = false;
        this.select(key);
        if (!this.dlg.open) this.dlg.showModal();
        lock(true);
        const t = this.tabs.find((x) => x.key === key);
        if (t && this.tabs.length > 1) t.tab.focus();
        requestAnimationFrame(() => this.dlg.classList.add('is-in'));
      }

      close() {
        if (!this.dlg.open || this.closing) return;
        this.closing = true;
        const ms = this.wait();
        this.dlg.classList.remove('is-in');
        const done = () => {
          if (!this.closing) return;
          this.closing = false;
          if (this.dlg.open) this.dlg.close();
          lock(false);
        };
        if (ms > 0) this.timer = setTimeout(done, ms);
        else done();
      }

      /* the panel's own transition time in ms (sheet 0.26s; pop-up and drawer 0.22s; 0 for reduced motion) */
      wait() {
        if (this.rm.matches) return 0;
        const cs = getComputedStyle(this.dlg);
        const durs = cs.transitionDuration.split(',');
        const dels = cs.transitionDelay.split(',');
        const s = durs.map((d, i) => (parseFloat(d) || 0) + (parseFloat(dels[i % dels.length]) || 0));
        return Math.max(0, ...s) * 1000;
      }
    }
  );
})();
